import React from 'react';
import clsx from 'clsx';
import ImageComponent from '../../../components/ImageComponent';
import type { IWithdrawText } from '../../../interfaces/Withdrawal';
import styles from '../styles.module.css';

interface WithdrawTextProps {
  data?: IWithdrawText;
  className?: string;
  /** Rendered when the server sends nothing for this slot. */
  fallback?: string;
}

/**
 * Some payloads put a Lottie filename in `iconUrl` — `amount_transfer.json` on
 * the withdrawal progress state, which Android plays from its lottie cache. In a
 * browser that resolves against the current path and fetches an HTML page into an
 * `<img>`, so only absolute URLs are treated as images.
 */
const isImageUrl = (value?: string) => Boolean(value && /^https?:\/\//i.test(value));

/**
 * Renders the server's universal label shape (`WithdrawBreakdownTextIconData`):
 * text plus an optional leading icon, coloured and aligned by `style`.
 *
 * `isHtml` content comes from our own BFF and is rendered as markup, matching
 * Android's `HtmlCompat.fromHtml` handling of the same payloads.
 */
const WithdrawText: React.FC<WithdrawTextProps> = ({
  data,
  className,
  fallback,
}) => {
  const text = data?.text ?? fallback;
  const iconUrl = isImageUrl(data?.iconUrl) ? data?.iconUrl : undefined;
  if (!text && !iconUrl) return null;

  const style = data?.style;
  const inlineStyle: React.CSSProperties = {
    ...(style?.textColor ? { color: style.textColor } : {}),
    ...(style?.bgColor ? { backgroundColor: style.bgColor } : {}),
    ...(style?.alignment ? { textAlign: style.alignment as never } : {}),
    ...(data?.isStrikeThrough ? { textDecoration: 'line-through' } : {}),
    ...(style?.textStyle === 'bold' ? { fontWeight: 700 } : {}),
  };

  return (
    <span className={clsx(styles.withdrawText, className)} style={inlineStyle}>
      {iconUrl && (
        <ImageComponent
          src={iconUrl}
          alt=""
          className={styles.withdrawTextIcon}
        />
      )}
      {data?.isHtml && text ? (
        <span dangerouslySetInnerHTML={{ __html: text }} />
      ) : (
        <span>{text}</span>
      )}
    </span>
  );
};

export default WithdrawText;
